import { TbArrowUpRight, TbMicrophone } from "solid-icons/tb";
import { type Component, For, Index, Show } from "solid-js";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import { useScene, useTyping } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import {
  ChatExchange,
  ChiefPane,
  PeopleStack,
  SessionPill,
  SessionWindow,
  startedLabel,
  type TranscriptEntry,
  TranscriptBlock,
} from "./internal/chief";
import { at, Cursor, MockScene, type Person } from "./internal/parts";
import { ScaledFrame } from "./ScaledFrame";

export type AskData = {
  project: string;
  session: string;
  started: string;
  clock: string;
  people: Person[];
  transcript: TranscriptEntry[];
  /** The transcript block the question is asked about. */
  selected: number;
  question: string;
  answer: string;
  sources: { kind: "session" | "notion"; label: string }[];
};

const T = { bubble: 0.8, typing: 1.1, cursor: 2.2, ask: 3.3, chat: 3.5, answer: 4.0, perWord: 0.06 };

/**
 * Asking mid-meeting, played once: a passage of the transcript is selected, the
 * highlight bubble takes the question, and the chat it opens in the Chief
 * pane answers with its sources.
 */
export const AskMockup: Component<{ data: AskData; class?: string }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const scene = useScene();
  const typed = useTyping(() => props.data.question, scene, T.typing * 1000);
  const words = () => props.data.answer.split(" ");
  const sourcesAt = () => T.answer + words().length * T.perWord + 0.2;

  return (
    <MockScene scene={scene} label={t("marketing.mock.ask.alt")} class={props.class}>
      <ScaledFrame chrome="app" width={960} height={520} narrow={{ width: 400, height: 700 }}>
        <SessionWindow
          project={props.data.project}
          title={props.data.session}
          facts={`${t("marketing.mock.live")} · ${startedLabel(props.data.started, currentLanguage())}`}
          live
          tab="transcript"
          bar={
            <SessionPill clock={props.data.clock} people={<PeopleStack people={props.data.people} />} wave={false} />
          }
          pane={
            <ChiefPane
              class="h-72.5 shrink-0 @2xl:h-auto @2xl:w-85"
              thread={{ title: props.data.question, at: T.chat }}
            >
              <div class="card-appear h-full p-3" style={at(T.chat)}>
                <ChatExchange
                  question={props.data.question}
                  answer={
                    <Index each={words()}>
                      {(word, i) => (
                        <span class="msg-in" style={at(T.answer + i * T.perWord)}>
                          {word()}{" "}
                        </span>
                      )}
                    </Index>
                  }
                  sources={props.data.sources.map((source) => ({
                    label: source.label,
                    icon:
                      source.kind === "notion" ? (
                        <BrandGlyph src="/images/logos/notion.svg" class="size-3.5 text-primary-text" />
                      ) : (
                        <TbMicrophone class="size-3.5 text-session-icon" aria-hidden="true" />
                      ),
                  }))}
                  sourcesAt={sourcesAt()}
                />
              </div>
            </ChiefPane>
          }
        >
          <div class="px-5 py-4">
            <For each={props.data.transcript}>
              {(entry, i) => (
                <Show
                  when={i() === props.data.selected}
                  fallback={<TranscriptBlock entry={entry} class={i() === 0 ? "hidden @2xl:block" : undefined} />}
                >
                  <SelectedBlock entry={entry} question={typed()} />
                </Show>
              )}
            </For>
          </div>
        </SessionWindow>
      </ScaledFrame>
    </MockScene>
  );
};

/**
 * The block the question is about: its text selected, and the highlight bubble
 * open under it, as the transcript draws them when a passage is selected.
 */
const SelectedBlock: Component<{ entry: TranscriptEntry; question: string }> = (props) => {
  const { t } = useI18n();
  return (
    <div class="relative">
      <TranscriptBlock entry={props.entry} />
      <div class="exit absolute inset-s-0 top-full z-10 -mt-1 w-75" style={at(T.ask + 0.2)}>
        <div
          class="card-appear flex flex-col gap-2 rounded-lg border border-primary-border bg-tile px-3 pt-1.5 pb-3 shadow-xl"
          style={at(T.bubble)}
        >
          <div class="flex justify-center pb-0.5">
            <span class="grid grid-cols-3 gap-0.5">
              <For each={[0, 1, 2, 3, 4, 5]}>{() => <span class="size-0.5 rounded-full bg-muted-text" />}</For>
            </span>
          </div>
          <span class="inline-flex self-start rounded-full border border-primary-border bg-panel p-0.5">
            <span class="rounded-full bg-tile px-3 py-1 text-xs font-semibold text-primary-text">
              {t("marketing.mock.ask.chat")}
            </span>
            <span class="rounded-full px-3 py-1 text-xs font-semibold text-muted-text">
              {t("marketing.mock.ask.note")}
            </span>
          </span>
          <div class="flex items-end gap-2">
            <span class="min-h-10 flex-1 rounded-md border border-(--mac-accent) bg-panel px-2.5 py-2 text-sm text-primary-text">
              {props.question}
              <span class="ms-px inline-block h-3.5 w-px translate-y-0.5 bg-primary-text" />
            </span>
            <span class="press relative" style={at(T.ask)}>
              <span class="mac-push mac-push-brand grid p-2">
                <TbArrowUpRight class="size-4" aria-hidden="true" />
              </span>
              <Cursor start={T.cursor} />
            </span>
          </div>
          <p class="text-[0.625rem] leading-snug text-muted-text">{t("marketing.mock.ask.hint")}</p>
        </div>
      </div>
    </div>
  );
};
